/**
 * <category-menu>: the Shop mega menu's category column, in two layouts
 * chosen by `(hover: hover) and (pointer: fine)`:
 *
 * - Flyout (mouse): the group list is always showing; hovering a group opens
 *   its subcategories in a panel to the right, over whatever is there, the
 *   way nested desktop menus work. Diagonal travel from a group to its panel crosses the groups below
 *   it, so a "safe triangle" keeps the panel open on the way: an SVG laid
 *   between the pointer and the panel's near edge, whose single path catches
 *   pointer events so the groups beneath it never see the pointer. It is
 *   redrawn on every pointer move and removed when the pointer reaches the
 *   panel, leaves the menu, or rests for a moment, at which point the group
 *   under the pointer takes over. After Costa Alexoglou, "Better Context Menus
 *   With Safe Triangles" (Smashing Magazine, 2023).
 *
 * - Slide (touch): pressing a group slides its panel in over the group list,
 *   behind a back button, the way the mobile sidebar does. Motion runs on the
 *   Web Animations API; the dropdown keeps the group list's height as a floor
 *   so it never shrinks under a finger or pointer that pressed low in the list.
 *
 * Keyboard works in both: Enter or ArrowRight on a group opens its panel and
 * moves into it; ArrowLeft or Escape returns to the group; ArrowUp/Down walk
 * a list. The dropdown's own open/close (hover intent, outside clicks, Escape
 * at the top level) stays with <dropdown-menu-disclosure>; this element owns
 * only which panel is showing, through one setter, activate().
 *
 * Markup contract (snippets/category-menu.liquid):
 *   .category-menu__track            the layout box (flex in flyout, a grid cell stack in slide)
 *     .category-menu__groups          [data-panel="root"]; group buttons carry aria-controls
 *     .category-menu__subs            one .category-menu__sub [data-panel="sub"] per group, hidden + inert
 *       [data-action="back"]          the slide layout's way back to the group list
 */
const SVG_NS = 'http://www.w3.org/2000/svg';
const SLIDE_DURATION = 300;
const SAFE_AREA_REST_MS = 300;
const POINTER_TRAIL_MS = 100;
// The flyout needs a mouse and room for its side panel (the images make way
// for it below 1250px); up to 829px, and on touch screens, the slide layout
// takes over. Below 1000px the site shows the sidebar instead of the mega
// menu, so those widths are only seen with ?megamenu. Keep in step with the
// media query in theme.css.
const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 830px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// ?safetriangle on any URL paints the safe area so it can be inspected.
const debugSafeArea = new URLSearchParams(window.location.search).has('safetriangle');

const readToken = (element, name, fallback) => getComputedStyle(element).getPropertyValue(name).trim() || fallback;

/**
 * The outermost ink, in viewport pixels, of a set of labels: the rightmost
 * right edge or the leftmost left edge of the letters themselves, not their
 * boxes. Each line of a label's text is located with a Range and its glyphs
 * measured on a canvas in the label's own font; a label that wraps is
 * measured line by line. Returns NaN when there is nothing to measure.
 */
const inkContext = document.createElement('canvas').getContext('2d');
const inkRange = document.createRange();

function inkEdge(elements, side) {
  let edge = side === 'right' ? -Infinity : Infinity;
  for (const element of elements) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode()) && !node.textContent.trim()) {}
    if (!node) continue;
    const style = getComputedStyle(node.parentElement);
    inkContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    // 'normal' is not a canvas value and would leave the last label's tracking in place.
    if ('letterSpacing' in inkContext) {
      inkContext.letterSpacing = style.letterSpacing === 'normal' ? '0px' : style.letterSpacing;
    }
    for (const [from, to] of textLines(node)) {
      const raw = node.textContent.slice(from, to);
      const text = raw.trim();
      if (!text) continue;
      const start = from + raw.length - raw.trimStart().length;
      inkRange.setStart(node, start);
      inkRange.setEnd(node, start + text.length);
      const box = inkRange.getBoundingClientRect();
      if (!box.width) continue;
      const metrics = inkContext.measureText(text);
      edge = side === 'right'
        ? Math.max(edge, box.left + metrics.actualBoundingBoxRight)
        : Math.min(edge, box.left - metrics.actualBoundingBoxLeft);
    }
  }
  return Number.isFinite(edge) ? edge : NaN;
}

// The [from, to) offsets of each rendered line of a text node.
function textLines(node) {
  const length = node.textContent.length;
  inkRange.selectNodeContents(node);
  if (inkRange.getClientRects().length <= 1) return [[0, length]];
  const lines = [];
  let lineStart = 0;
  let lineTop = null;
  for (let i = 0; i < length; i += 1) {
    inkRange.setStart(node, i);
    inkRange.setEnd(node, i + 1);
    const rect = inkRange.getBoundingClientRect();
    if (!rect.height) continue; // a collapsed space at the break
    if (lineTop !== null && rect.top - lineTop > rect.height / 2) {
      lines.push([lineStart, i]);
      lineStart = i;
    }
    if (lineTop === null || rect.top - lineTop > rect.height / 2) lineTop = rect.top;
  }
  lines.push([lineStart, length]);
  return lines;
}

class CategoryMenu extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._activePanel = null;
    this._animations = [];
    this._epoch = 0;
    this._rootHeight = 0;
    this._safeArea = null;
    this._safeAreaTimer = null;
    this._lastPointer = null;
    this._pointerTrail = [];
    this._ruleObserver = null;
    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onPointerOver = this._onPointerOver.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._onDisclosureToggle = this._onDisclosureToggle.bind(this);
    this._onLayoutChange = this._onLayoutChange.bind(this);
  }

  connectedCallback() {
    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this.addEventListener('click', this._onClick, { signal });
    this.addEventListener('keydown', this._onKeydown, { signal });
    this.addEventListener('pointerover', this._onPointerOver, { signal });
    this.addEventListener('pointermove', this._onPointerMove, { passive: true, signal });
    this.addEventListener('pointerleave', this._onPointerLeave, { signal });
    hoverCapable.addEventListener('change', this._onLayoutChange, { signal });
    // The disclosure removes "open" once its hide animation ends; resetting
    // then means the next open always starts on the group list.
    this.closest('details')?.addEventListener('toggle', this._onDisclosureToggle, { signal });
    // In the sidebar, the next time the drawer opens starts on the group list too.
    this.closest('header-sidebar')?.addEventListener('dialog:after-hide', () => this.reset(), { signal });

    this._observeRule();
    this.reset();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._abortController = null;
    this._cancelAnimations();
    this._removeSafeArea();
    this._ruleObserver?.disconnect();
    this._ruleObserver = null;
  }

  /**
   * The mega menu keeps at least 70px between its hairline and the columns
   * on both sides, decided by measurement rather than breakpoints, whenever
   * the panel, the exhibitor list or this column changes size (which
   * includes the panel opening):
   *
   * - Wide: the line on its grid column and this column centred, with the
   *   exhibitor list's two columns spaced so the names end as far before the
   *   line as this column starts after it. Only a measurement can find where
   *   the names end, so the gap is written to --mega-menu-exhibitor-gap.
   * - Flow (.mega-menu--flow): when that would need less than 70px on a side
   *   or an exhibitor gap under 40px, the row re-flows left to right with a
   *   141px gutter whose centre carries the line (--mega-menu-rule-x).
   */
  _observeRule() {
    const megaMenu = this.closest('.mega-menu');
    const exhibitors = megaMenu?.querySelector('.mega-menu__linklist-item--columns');
    const columns = exhibitors?.querySelector('.mega-menu__columns');
    const column = this.closest('.mega-menu__categories');
    if (!megaMenu || !exhibitors || !columns || !column) {
      return;
    }
    const MIN_GAP = 40;
    const MIN_CLEARANCE = 70;
    // Below this the panel need not follow the layout grid's columns at all.
    const WIDE_FROM = 1700;
    const balance = () => {
      if (!column.getBoundingClientRect().width || !columns.lastElementChild) {
        return; // Closed: nothing to measure yet.
      }
      // Measure the wide layout from its defaults; the flow layout may be on.
      megaMenu.classList.remove('mega-menu--flow');
      megaMenu.style.removeProperty('--mega-menu-rule-x');
      columns.style.removeProperty('--mega-menu-exhibitor-gap');
      const panelLeft = megaMenu.getBoundingClientRect().left;
      const line = panelLeft + parseFloat(getComputedStyle(megaMenu, '::before').left) + 0.5;
      const defaultGap = parseFloat(getComputedStyle(columns).columnGap);
      const namesRight = columns.lastElementChild.getBoundingClientRect().right;
      const clearance = column.getBoundingClientRect().left - line;
      // The names' right edge moves one for one with the gap and the category
      // column does not move at all, so the balancing gap is a direct solve.
      const gap = Math.round((defaultGap + (line - clearance - namesRight)) * 2) / 2;

      if (window.innerWidth >= WIDE_FROM && clearance >= MIN_CLEARANCE && gap >= MIN_GAP) {
        columns.style.setProperty('--mega-menu-exhibitor-gap', `${gap}px`);
        capPromo();
        return;
      }
      megaMenu.classList.add('mega-menu--flow');
      // The line goes midway between the exhibitor names and this column, by
      // their ink rather than their boxes: the letters sit inside their boxes
      // with their own side bearings, so box-to-box symmetry still looks
      // uneven. The column follows the gutter (two clearances and the line,
      // theme.css's --mega-menu-rule-clearance), including when the images
      // are hidden, so the line stays one clearance from each column.
      const namesInkRight = inkEdge(exhibitors.querySelectorAll('a'), 'right');
      const categoriesInkLeft = inkEdge(
        column.querySelectorAll(':scope > .h6, .category-menu__groups .category-menu__item'),
        'left'
      );
      const ruleX = Number.isFinite(namesInkRight) && Number.isFinite(categoriesInkLeft)
        ? (namesInkRight + categoriesInkLeft - 1) / 2 - panelLeft
        : (exhibitors.getBoundingClientRect().right + column.getBoundingClientRect().left - 1) / 2 - panelLeft;
      megaMenu.style.setProperty('--mega-menu-rule-x', `${Math.round(ruleX * 2) / 2}px`);
      capPromo();
    };
    // The photos' height is set by the text columns beside them and their
    // width by the room left over, so on a wide screen they stretched into
    // thin strips. Their width is capped at their height times
    // --mega-menu-promo-max-ratio (theme.css). A narrower group can wrap its
    // heading and so shorten the photos, so the cap is settled in a few passes.
    const promoGroup = megaMenu.querySelector('.mega-menu__promo-group');
    const capPromo = () => {
      const tile = promoGroup?.querySelector('.mega-menu__promo > *');
      const ratio = tile && parseFloat(getComputedStyle(promoGroup).getPropertyValue('--mega-menu-promo-max-ratio'));
      if (!ratio) {
        return;
      }
      promoGroup.style.removeProperty('--mega-menu-promo-max-width');
      for (let pass = 0; pass < 3; pass += 1) {
        const height = tile.getBoundingClientRect().height;
        if (!height) {
          return; // Hidden at this width.
        }
        const width = `${Math.floor(height * ratio)}px`;
        if (promoGroup.style.getPropertyValue('--mega-menu-promo-max-width') === width) {
          return;
        }
        promoGroup.style.setProperty('--mega-menu-promo-max-width', width);
      }
    };
    this._ruleObserver = new ResizeObserver(balance);
    [megaMenu, exhibitors, column].forEach((element) => this._ruleObserver.observe(element));
    balance();
    // The ink is measured in the web font, so measure again once it is in.
    document.fonts?.ready.then(balance);
  }

  // The copy in the mobile sidebar (slide-only) always slides.
  get isFlyout() {
    return !this.hasAttribute('slide-only') && hoverCapable.matches;
  }

  get trackElement() {
    return this.querySelector('.category-menu__track');
  }

  get rootPanel() {
    return this.querySelector('[data-panel="root"]');
  }

  get subPanels() {
    return Array.from(this.querySelectorAll('[data-panel="sub"]'));
  }

  get groupButtons() {
    return Array.from(this.rootPanel.querySelectorAll('[aria-controls]'));
  }

  panelFor(button) {
    return this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
  }

  buttonFor(panel) {
    return this.groupButtons.find((button) => button.getAttribute('aria-controls') === panel.id) || null;
  }

  /**
   * Show one sub panel (or none when null). The only place panel visibility,
   * inertness, aria-expanded and focus change together, in either layout.
   */
  activate(panel, { focus = false } = {}) {
    if (this.isFlyout) {
      this._showFlyout(panel, focus);
    } else {
      this._slideTo(panel);
    }
  }

  /**
   * Instant return to the resting state, used when the menu closes: the group
   * list alone in the slide layout, the first group's panel in the flyout.
   */
  reset() {
    this._epoch += 1;
    this._cancelAnimations();
    this._removeSafeArea();
    this._activePanel = null;
    this._rootHeight = 0;
    this.trackElement.style.minHeight = '';
    this.removeAttribute('data-flyout-open');

    const root = this.rootPanel;
    root.hidden = false;
    root.inert = false;
    this.subPanels.forEach((sub) => {
      sub.hidden = true;
      sub.inert = true;
      sub.classList.remove('is-inactive');
    });
    this.groupButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));

    const first = this.groupButtons[0];
    if (this.isFlyout && first) {
      this._showFlyout(this.panelFor(first), false);
    }
  }

  _setExpanded(panel) {
    this.groupButtons.forEach((button) => {
      button.setAttribute('aria-expanded', String(panel !== null && button.getAttribute('aria-controls') === panel.id));
    });
  }

  // --- Flyout layout -------------------------------------------------------

  /**
   * One sub panel is always showing in this layout, so null never hides it:
   * it only returns focus to the showing panel's group. Inactive panels stay
   * displayed but invisible (is-inactive), keeping the column's width.
   */
  _showFlyout(panel, focus) {
    const previous = this._activePanel;

    if (panel !== null && panel !== previous) {
      this._activePanel = panel;
      this.subPanels.forEach((sub) => {
        const active = sub === panel;
        sub.hidden = false;
        sub.inert = !active;
        sub.classList.toggle('is-inactive', !active);
      });
      this._setExpanded(panel);
      this.setAttribute('data-flyout-open', '');
    }

    if (focus) {
      const target = panel ? panel.querySelector('ul a') : this._activePanel && this.buttonFor(this._activePanel);
      target?.focus({ preventScroll: true });
    }
  }

  _onPointerOver(event) {
    if (!this.isFlyout || event.pointerType !== 'mouse') {
      return;
    }
    // While the safe area is under the pointer, events target its path, not
    // the group beneath it, so no switch happens here.
    this._showFlyoutForRow(event.target);
  }

  /** The hover zone is the whole row; a row without subcategories leaves the showing panel as it is. */
  _showFlyoutForRow(target) {
    const row = target instanceof Element ? target.closest('.category-menu__groups li') : null;
    const button = row && this.contains(row) ? row.querySelector('[aria-controls]') : null;
    if (button) {
      this._showFlyout(this.panelFor(button), false);
    }
  }

  _onPointerMove(event) {
    if (!this.isFlyout || event.pointerType !== 'mouse' || !this._activePanel) {
      return;
    }
    const point = { x: event.clientX, y: event.clientY, t: event.timeStamp };
    this._lastPointer = point;
    this._pointerTrail = this._pointerTrail.filter((p) => point.t - p.t <= POINTER_TRAIL_MS);
    this._pointerTrail.push(point);

    if (this._activePanel.contains(event.target)) {
      this._removeSafeArea();
      return;
    }
    const overGroups = this.rootPanel.contains(event.target) || (this._safeArea !== null && this._safeArea.contains(event.target));
    if (!overGroups) {
      return;
    }

    // The triangle exists only while the pointer is heading for the panel.
    // Moving down the list (or away) drops it at once and hands the row under
    // the pointer the hover, so no row is dead for the 300ms the rest timer
    // would take. Direction is read over the last 100ms, so the pixel steps
    // of a diagonal move that happen to be vertical do not break the shield.
    const movingToPanel = point.x - this._pointerTrail[0].x > 2;
    if (movingToPanel) {
      this._drawSafeArea(point.x, point.y);
    } else if (this._safeArea !== null) {
      this._removeSafeArea();
      this._showFlyoutForRow(document.elementFromPoint(point.x, point.y));
    }
  }

  _onPointerLeave() {
    this._removeSafeArea();
  }

  /**
   * The safe area is an SVG spanning from the pointer to the flyout's near
   * edge, as tall as the flyout; its path is the triangle from the pointer to
   * that edge's two corners. It is positioned within this element, the
   * dropdown's absolutely positioned box, so it scrolls with it.
   */
  _drawSafeArea(pointerX, pointerY) {
    const flyoutRect = this._activePanel.getBoundingClientRect();
    const width = flyoutRect.left - pointerX + 2;
    if (width <= 0) {
      this._removeSafeArea();
      return;
    }

    if (this._safeArea === null) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.classList.add('category-menu__safe-area');
      svg.setAttribute('aria-hidden', 'true');
      if (debugSafeArea) {
        svg.dataset.debug = '';
      }
      svg.append(document.createElementNS(SVG_NS, 'path'));
      this.append(svg);
      this._safeArea = svg;
    }

    const menuRect = this.getBoundingClientRect();
    const height = flyoutRect.height;
    const svg = this._safeArea;
    svg.style.left = `${pointerX - 2 - menuRect.left}px`;
    svg.style.top = `${flyoutRect.top - menuRect.top + this.scrollTop}px`;
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;
    svg.firstElementChild.setAttribute('d', `M 0,${pointerY - flyoutRect.top} L ${width},${height} L ${width},0 z`);

    // A pointer resting inside the triangle is no longer travelling to the
    // flyout, so the row under it takes over right away rather than waiting
    // for the next movement to cross into it.
    clearTimeout(this._safeAreaTimer);
    this._safeAreaTimer = setTimeout(() => {
      this._removeSafeArea();
      if (this._lastPointer) {
        this._showFlyoutForRow(document.elementFromPoint(this._lastPointer.x, this._lastPointer.y));
      }
    }, SAFE_AREA_REST_MS);
  }

  _removeSafeArea() {
    clearTimeout(this._safeAreaTimer);
    this._safeAreaTimer = null;
    this._safeArea?.remove();
    this._safeArea = null;
  }

  // --- Slide layout --------------------------------------------------------

  async _slideTo(panel) {
    if (panel === this._activePanel) {
      return;
    }

    const epoch = ++this._epoch;
    const track = this.trackElement;
    const root = this.rootPanel;
    const fromPanel = this._activePanel ?? root;
    const toPanel = panel ?? root;
    const forward = panel !== null;
    const fromStyle = getComputedStyle(fromPanel);
    const fromFrame = { transform: fromStyle.transform, opacity: fromStyle.opacity };
    const toStyle = !toPanel.hidden && getComputedStyle(toPanel);
    const toFrame = toStyle && { transform: toStyle.transform, opacity: toStyle.opacity };
    const fromHeight = track.getBoundingClientRect().height;
    // Reversing mid-slide starts at the painted position and height, not at
    // the previous screen's full size or the beginning of its animation.
    this._cancelAnimations();
    this._activePanel = panel;
    this._setExpanded(panel);

    // A cancelled return slide may leave a third screen displayed. Only the
    // current pair may participate in this transition, even after rapid taps.
    [root, ...this.subPanels].forEach((screen) => {
      if (screen !== fromPanel && screen !== toPanel) {
        screen.hidden = true;
        screen.inert = true;
      }
    });

    // Measure the outgoing panel before anything moves, then reveal the
    // incoming one so it can be measured too. Both stay in the track's single
    // grid cell for the duration of the slide.
    toPanel.hidden = false;
    toPanel.inert = false;

    // The dropdown never gets shorter than the group list while it is open:
    // shrinking to a shorter sub list would pull the panel out from under a
    // pointer that pressed low in the group list, which the disclosure reads
    // as leaving the menu. Held as min-height so it survives the animation.
    const sidebar = this.closest('header-sidebar');
    if (fromPanel === root && !sidebar) {
      this._rootHeight = fromHeight;
    }
    track.style.minHeight = this._rootHeight ? `${this._rootHeight}px` : '';
    const toHeight = Math.max(toPanel.offsetHeight, this._rootHeight);

    // On phones the drawer's shadow content is the scroll container; at wider
    // widths Prestige uses the panel scroller. Start the incoming screen at
    // the top in either layout without scrolling the page behind the drawer.
    if (sidebar) {
      sidebar.getShadowPartByName('content').scrollTop = 0;
      this.closest('.header-sidebar__scroller').scrollTop = 0;
    }

    // Focus must land in the incoming panel before the outgoing one goes
    // inert, or it drops to <body> and the disclosure closes itself on focusout.
    const focusTarget = forward ? toPanel.querySelector('[data-action="back"]') : this.buttonFor(fromPanel);
    focusTarget?.focus({ preventScroll: true });
    fromPanel.inert = true;

    const flip = readToken(this, '--transform-logical-flip', '1');
    const options = {
      duration: reducedMotion.matches ? 0 : SLIDE_DURATION,
      easing: readToken(document.documentElement, '--cubicBez', 'cubic-bezier(.36, .01, .1, 1.01)'),
      fill: 'forwards',
    };
    const direction = forward ? 1 : -1;

    this._animations = [
      track.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], options),
      fromPanel.animate([fromFrame, { transform: `translateX(calc(${flip} * ${-100 * direction}%))`, opacity: 0 }], options),
      toPanel.animate([toFrame || { transform: `translateX(calc(${flip} * ${100 * direction}%))`, opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }], options),
    ];

    try {
      await Promise.all(this._animations.map((animation) => animation.finished));
    } catch (error) {
      return; // Cancelled by a newer switch or a reset, which already owns the state.
    }

    if (epoch !== this._epoch) {
      return;
    }

    // Hide the outgoing panel first, then drop the forwards fills: the track
    // then sizes itself to the only displayed panel, so nothing jumps.
    fromPanel.hidden = true;
    this._cancelAnimations();
    if (sidebar && !forward && document.activeElement === focusTarget) {
      focusTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }
  }

  _cancelAnimations() {
    this._animations.forEach((animation) => animation.cancel());
    this._animations = [];
  }

  // --- Shared input --------------------------------------------------------

  _onClick(event) {
    const trigger = event.target.closest('[data-action="back"], [aria-controls]');
    if (!trigger || !this.contains(trigger)) {
      return;
    }
    if (trigger.dataset.action === 'back') {
      this.activate(null);
      return;
    }
    // A keyboard press (detail 0) moves into the panel; a pointer or touch
    // press only opens it. The slide layout always moves focus itself.
    this.activate(this.panelFor(trigger), { focus: event.detail === 0 });
  }

  _onKeydown(event) {
    const button = event.target.closest('[aria-controls]');
    const inSubPanel = this._activePanel !== null && this._activePanel.contains(event.target);

    if (button && this.contains(button) && event.key === 'ArrowRight') {
      event.preventDefault();
      this.activate(this.panelFor(button), { focus: true });
    } else if (inSubPanel && (event.key === 'ArrowLeft' || event.key === 'Escape')) {
      // Inside a sub panel Escape only backs out of it. Stopping propagation
      // keeps the disclosure's own Escape handler, which closes the whole
      // menu, for the group list.
      event.preventDefault();
      event.stopPropagation();
      this.activate(null, { focus: true });
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const list = event.target.closest('ul');
      if (!list || !this.contains(list)) {
        return;
      }
      const items = Array.from(list.querySelectorAll('a, button'));
      const index = items.indexOf(event.target);
      if (index === -1) {
        return;
      }
      event.preventDefault();
      items[(index + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus({ preventScroll: true });
    }
  }

  _onDisclosureToggle(event) {
    if (!event.currentTarget.open) {
      this.reset();
    }
  }

  _onLayoutChange() {
    if (!this.hasAttribute('slide-only')) {
      this.reset();
    }
  }
}

if (!window.customElements.get('category-menu')) {
  window.customElements.define('category-menu', CategoryMenu);
}
